import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { profileAPI } from '../../services/api'
import {
  CalculatorIcon,
  CheckIcon,
  ArrowPathIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline'

const GOAL_TYPES = [
  { value: 'lose_weight', label: 'Lose Weight', icon: '📉' },
  { value: 'maintain', label: 'Maintain', icon: '⚖️' },
  { value: 'gain_muscle', label: 'Gain Muscle', icon: '💪' },
  { value: 'custom', label: 'Custom', icon: '🎯' }
]

const ACTIVITY_LEVELS = [
  { value: 'sedentary', label: 'Sedentary' },
  { value: 'light', label: 'Light' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'active', label: 'Active' },
  { value: 'very_active', label: 'Very Active' }
]

// VH-62: Validation ranges
const VALIDATION = {
  targetWeight: { min: 30, max: 300 },
  weeklyChange: { loseMin: -1.0, loseMax: 0, gainMin: 0, gainMax: 1.0 }
}

function validateField(field, value, goalType, t) {
  if (value === '' || value === null || value === undefined) return null // optional fields
  const num = parseFloat(value)
  if (isNaN(num)) return t('goals.errors.mustBeNumber', 'Must be a number')
  if (field === 'targetWeight') {
    if (num < VALIDATION.targetWeight.min || num > VALIDATION.targetWeight.max) {
      return t('goals.errors.weightRange', 'Weight: {{min}}-{{max}} kg', {
        min: VALIDATION.targetWeight.min, max: VALIDATION.targetWeight.max
      })
    }
    if (num < 0) return t('goals.errors.mustBePositive', 'Must be a positive number')
  }
  if (field === 'weeklyChange') {
    if (goalType === 'lose_weight' && (num < VALIDATION.weeklyChange.loseMin || num > VALIDATION.weeklyChange.loseMax)) {
      return t('goals.errors.weeklyLossRange', 'Weekly loss: {{min}} to {{max}} kg', {
        min: VALIDATION.weeklyChange.loseMin, max: VALIDATION.weeklyChange.loseMax
      })
    }
    if (goalType === 'gain_muscle' && (num < VALIDATION.weeklyChange.gainMin || num > VALIDATION.weeklyChange.gainMax)) {
      return t('goals.errors.weeklyGainRange', 'Weekly gain: {{min}} to {{max}} kg', {
        min: VALIDATION.weeklyChange.gainMin, max: VALIDATION.weeklyChange.gainMax
      })
    }
  }
  return null
}

export default function GoalCalculator({ onGoalCreated }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const [goalType, setGoalType] = useState('maintain')
  const [activityLevel, setActivityLevel] = useState('moderate')
  const [targetWeight, setTargetWeight] = useState('')
  const [weeklyChange, setWeeklyChange] = useState('')
  const [calculatedGoal, setCalculatedGoal] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [apiError, setApiError] = useState('')

  // Calculate goals mutation
  const calculateMutation = useMutation({
    mutationFn: (data) => profileAPI.calculateGoals(data),
    onSuccess: (response) => {
      setCalculatedGoal(response.data)
      setApiError('')
    },
    onError: (error) => {
      const detail = error.response?.data?.detail
      if (typeof detail === 'string') {
        setApiError(detail)
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors
        const errors = {}
        detail.forEach((err) => {
          const field = err.loc?.[err.loc.length - 1]
          if (field) errors[field] = err.msg
        })
        setFieldErrors(errors)
      } else {
        setApiError(t('common.error', 'An error occurred'))
      }
    }
  })

  // Create goal mutation
  const createGoalMutation = useMutation({
    mutationFn: (data) => profileAPI.createGoal(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentGoal'] })
      queryClient.invalidateQueries({ queryKey: ['profileSummary'] })
      setCalculatedGoal(null)
      if (onGoalCreated) onGoalCreated()
    }
  })

  const handleCalculate = () => {
    // VH-62: Client-side validation
    const errors = {}
    if (goalType === 'lose_weight' || goalType === 'gain_muscle') {
      const twError = validateField('targetWeight', targetWeight, goalType, t)
      if (twError) errors.targetWeight = twError
      const wcError = validateField('weeklyChange', weeklyChange, goalType, t)
      if (wcError) errors.weeklyChange = wcError
    }

    setFieldErrors(errors)
    setApiError('')
    if (Object.keys(errors).length > 0) return

    const data = {
      goal_type: goalType,
      activity_level: activityLevel
    }
    if (targetWeight) data.target_weight_kg = parseFloat(targetWeight)
    if (weeklyChange) data.weekly_weight_change_kg = parseFloat(weeklyChange)

    calculateMutation.mutate(data)
  }

  const handleSaveGoal = () => {
    if (!calculatedGoal) return

    createGoalMutation.mutate({
      goal_type: calculatedGoal.goal_type,
      target_calories: calculatedGoal.target_calories,
      target_protein_g: calculatedGoal.target_protein_g,
      target_carbs_g: calculatedGoal.target_carbs_g,
      target_fat_g: calculatedGoal.target_fat_g,
      activity_level: calculatedGoal.activity_level,
      target_weight_kg: targetWeight ? parseFloat(targetWeight) : null,
      weekly_weight_change_kg: weeklyChange ? parseFloat(weeklyChange) : null,
      source: 'calculated'
    })
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex items-center">
          <CalculatorIcon className="h-6 w-6 text-indigo-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">
            {t('profile.goalCalculator', 'Goal Calculator')}
          </h3>
        </div>
      </div>
      <div className="card-body space-y-6">
        {/* Goal Type Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('profile.goalType', 'What is your goal?')}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {GOAL_TYPES.map(type => (
              <button
                key={type.value}
                onClick={() => setGoalType(type.value)}
                className={`p-3 rounded-lg border-2 text-left transition-colors ${
                  goalType === type.value
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className="text-xl mr-2">{type.icon}</span>
                <span className="font-medium">
                  {t(`profile.goalTypes.${type.value}`, type.label)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Activity Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('profile.activityLevel', 'Activity Level')}
          </label>
          <select
            value={activityLevel}
            onChange={(e) => setActivityLevel(e.target.value)}
            className="form-select w-full"
          >
            {ACTIVITY_LEVELS.map(level => (
              <option key={level.value} value={level.value}>
                {t(`profile.activityLevels.${level.value}`, level.label)}
              </option>
            ))}
          </select>
        </div>

        {/* Optional fields for weight goals */}
        {(goalType === 'lose_weight' || goalType === 'gain_muscle') && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.targetWeight', 'Target Weight (kg)')}
                <span className="text-gray-400 ml-1">({t('common.optional', 'optional')})</span>
              </label>
              <input
                type="number"
                value={targetWeight}
                onChange={(e) => {
                  setTargetWeight(e.target.value)
                  setFieldErrors((prev) => ({ ...prev, targetWeight: null }))
                }}
                className={`form-input ${fieldErrors.targetWeight ? 'border-red-400 focus:ring-red-400' : ''}`}
                placeholder="e.g., 75"
                step="0.1"
              />
              {fieldErrors.targetWeight && (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <ExclamationCircleIcon className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                  {fieldErrors.targetWeight}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('profile.weeklyChange', 'Weekly Weight Change (kg)')}
                <span className="text-gray-400 ml-1">({t('common.optional', 'optional')})</span>
              </label>
              <input
                type="number"
                value={weeklyChange}
                onChange={(e) => {
                  setWeeklyChange(e.target.value)
                  setFieldErrors((prev) => ({ ...prev, weeklyChange: null }))
                }}
                className={`form-input ${fieldErrors.weeklyChange ? 'border-red-400 focus:ring-red-400' : ''}`}
                placeholder={goalType === 'lose_weight' ? 'e.g., -0.5' : 'e.g., 0.25'}
                step="0.1"
                min={goalType === 'lose_weight' ? '-1' : '0'}
                max={goalType === 'lose_weight' ? '0' : '1'}
              />
              {fieldErrors.weeklyChange ? (
                <p className="text-xs text-red-600 mt-1 flex items-center">
                  <ExclamationCircleIcon className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                  {fieldErrors.weeklyChange}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  {goalType === 'lose_weight'
                    ? t('profile.safeWeightLoss', 'Safe loss: -0.5 to -1.0 kg/week')
                    : t('profile.safeWeightGain', 'Lean gain: 0.25 to 0.5 kg/week')}
                </p>
              )}
            </div>
          </>
        )}

        {/* Calculate Button */}
        <button
          onClick={handleCalculate}
          disabled={calculateMutation.isPending}
          className="btn-primary w-full"
        >
          {calculateMutation.isPending ? (
            <>
              <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
              {t('common.calculating', 'Calculating...')}
            </>
          ) : (
            <>
              <CalculatorIcon className="h-5 w-5 mr-2" />
              {t('profile.calculateGoals', 'Calculate Goals')}
            </>
          )}
        </button>

        {/* Error */}
        {apiError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 flex items-start">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
            <span>{apiError}</span>
          </div>
        )}

        {/* Results */}
        {calculatedGoal && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
            <h4 className="font-semibold text-green-800">
              {t('profile.calculatedGoals', 'Calculated Goals')}
            </h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-surface-container rounded-lg p-3">
                <p className="text-sm text-gray-500">{t('profile.targetCalories', 'Daily Calories')}</p>
                <p className="text-xl font-bold text-gray-900">
                  {calculatedGoal.target_calories} kcal
                </p>
              </div>
              <div className="bg-surface-container rounded-lg p-3">
                <p className="text-sm text-gray-500">{t('profile.protein', 'Protein')}</p>
                <p className="text-xl font-bold text-gray-900">
                  {Math.round(calculatedGoal.target_protein_g)}g
                </p>
                <p className="text-xs text-gray-500">
                  {calculatedGoal.protein_percentage}% | {calculatedGoal.protein_per_kg}g/kg
                </p>
              </div>
              <div className="bg-surface-container rounded-lg p-3">
                <p className="text-sm text-gray-500">{t('profile.carbs', 'Carbs')}</p>
                <p className="text-xl font-bold text-gray-900">
                  {Math.round(calculatedGoal.target_carbs_g)}g
                </p>
                <p className="text-xs text-gray-500">{calculatedGoal.carbs_percentage}%</p>
              </div>
              <div className="bg-surface-container rounded-lg p-3">
                <p className="text-sm text-gray-500">{t('profile.fat', 'Fat')}</p>
                <p className="text-xl font-bold text-gray-900">
                  {Math.round(calculatedGoal.target_fat_g)}g
                </p>
                <p className="text-xs text-gray-500">{calculatedGoal.fat_percentage}%</p>
              </div>
            </div>

            <div className="text-sm text-gray-600">
              <p>TDEE: {Math.round(calculatedGoal.tdee)} kcal</p>
              {calculatedGoal.calorie_adjustment !== 0 && (
                <p>
                  {t('profile.adjustment', 'Adjustment')}: {calculatedGoal.calorie_adjustment > 0 ? '+' : ''}{calculatedGoal.calorie_adjustment} kcal
                </p>
              )}
            </div>

            {/* Recommendations */}
            {calculatedGoal.recommendations?.length > 0 && (
              <div className="space-y-2">
                <p className="font-medium text-gray-700">
                  {t('profile.recommendations', 'Recommendations')}:
                </p>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  {calculatedGoal.recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={handleSaveGoal}
              disabled={createGoalMutation.isPending}
              className="btn-success w-full"
            >
              {createGoalMutation.isPending ? (
                t('common.saving', 'Saving...')
              ) : (
                <>
                  <CheckIcon className="h-5 w-5 mr-2" />
                  {t('profile.setAsMyGoal', 'Set as My Goal')}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
